import { Badge, Box, Flex, Overlay, Text, UnreadDot } from '@/components/common';
import { ProfilePhoto, UserDistanceLabel } from '@/components/profile';
import { useCurrentUser } from '@/contexts';
import { calculateAge, UserProfile } from '@/types';
import { firstPhotoUrl } from '@/utils';
import { UserIntentionsComponent } from './UserIntentionsComponent';

interface UserCardProps {
  user: UserProfile;
  currentUser: UserProfile | null;
  onClick: () => void;
}

export default function UserCard({ user, currentUser, onClick }: UserCardProps) {
  const { settings } = useCurrentUser();

  const age = user.age ?? calculateAge(user.birthday);

  if (!user.photos) return null;

  const photo = firstPhotoUrl(user.photos);
  const locationAllowed = settings?.locationAllowed ?? false;

  return (
    <Box
      asChild
      position="relative"
      overflow="hidden"
      className="group cursor-pointer"
      style={{
        borderRadius: 'var(--radius-3)',
      }}
      id={`discover-card-${user.uid}`}
    >
      <button onClick={onClick}>
        <ProfilePhoto
          src={photo}
          name={user.name}
          index={0}
          className="transition-transform duration-500 group-hover:scale-105"
        />

        <Overlay />

        <UserIntentionsComponent user={user} currentUser={currentUser} />

        {user.availableToday && (
          <UnreadDot
            color="green"
            position="absolute"
            size="4"
            data-testid="live-dot"
            style={{
              top: 8,
              right: 8,
            }}
          />
        )}

        <Flex
          position="absolute"
          bottom="0"
          left="0"
          right="0"
          direction="column"
          gap="1"
          px="1"
          pb="1"
        >
          <Flex align="baseline">
            <Box asChild flexShrink="1">
              <Text size="2" weight="medium" truncate>
                {user.name}
              </Text>
            </Box>
            <Box asChild flexShrink="0">
              <Text size="2" weight="medium" wrap="nowrap">
                {`, ${age}`}
              </Text>
            </Box>
          </Flex>
          <Flex align="center" gap="1" wrap="wrap">
            {user.lastActive && <Badge color="black">{user.lastActive}</Badge>}
            {locationAllowed && <UserDistanceLabel user={user} />}
          </Flex>
        </Flex>
      </button>
    </Box>
  );
}
